/**
 * Single HTTP boundary for the app (DIP). Pages/hooks import named APIs or default `api`;
 * do not instantiate axios or spread API_URL in feature code — extend exports here instead.
 */
import axios from 'axios';
import API_URL from '../config/api';
import { getStoredAuthToken, getStoredOrganizationId } from '../utils/authStorage';

// Normalize API URL - remove trailing /api if present since we add it in paths
// This prevents double /api/api/ prefix issues
const normalizeBaseURL = (url) => {
  if (!url) return url;
  // Remove trailing /api to prevent double prefix
  return url.replace(/\/api\/?$/, '');
};

// Create axios instance with default config
const api = axios.create({
  baseURL: normalizeBaseURL(API_URL),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and fix double /api/api/ prefix
api.interceptors.request.use(
  (config) => {
    if (!config.skipAuth) {
      const token = getStoredAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const organizationId = getStoredOrganizationId();
      if (organizationId) {
        config.headers['X-Organization-Id'] = organizationId;
      }
    }

    // FormData must not use application/json — browser sets multipart boundary automatically
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type');
      } else if (h) {
        delete h['Content-Type'];
      }
    }
    
    // Safety check: Fix double /api/api/ prefix if it exists
    if (config.url && config.url.includes('/api/api/')) {
      console.warn('⚠️ Detected double /api/api/ prefix, fixing:', config.url);
      config.url = config.url.replace(/\/api\/api\//g, '/api/');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// API Service functions for new schema

// Students API
export const studentsAPI = {
  getAll: (config) => api.get('/api/students', config),
  getById: (id) => api.get(`/api/students/${id}`),
  getByEmail: (email) =>
    api.get('/api/students/by-email', { params: { email: String(email || '').trim() } }),
  getProfile: (id) => api.get(`/api/students/${id}/profile`),
  getSoftDeleted: () => api.get('/api/students/soft-deleted'),
  create: (data) => api.post('/api/students', data),
  update: (id, data) => api.put(`/api/students/${id}`, data),
  /** Student self-service: personal + education only */
  updateMe: (data) => api.patch('/api/students/me', data),
  getChangeLogs: (id, params) => api.get(`/api/students/${id}/change-logs`, { params }),
  delete: (id) => api.delete(`/api/students/${id}`),
  hardDelete: (id) => api.delete(`/api/students/${id}/hard`),
  filter: (params) => api.get('/api/students/filter', { params }),
  completenessStats: () => api.get('/api/students/completeness/stats'),
  completenessList: (params) => api.get('/api/students/completeness', { params }),
  updateHighestDegreeYear: (id, data) =>
    api.patch(`/api/students/${id}/highest-degree-year`, data),
};

// Batches API
export const batchesAPI = {
  getAll: () => api.get('/api/batches'),
  getById: (id) => api.get(`/api/batches/${id}`),
  getByCourse: (courseId) => api.get(`/api/batches/course/${courseId}`),
  getStatistics: (id) => api.get(`/api/batches/${id}/statistics`),
  create: (data) => api.post('/api/batches', data),
  ensure: (data) => api.post('/api/batches/ensure', data),
  update: (id, data) => api.put(`/api/batches/${id}`, data),
  delete: (id) => api.delete(`/api/batches/${id}`),
  getTrainerOptions: () => api.get('/api/batches/trainers/options'),
  getTrainers: (batchId) => api.get(`/api/batches/${batchId}/trainers`),
  linkTrainer: (batchId, trainerId) =>
    api.post(`/api/batches/${batchId}/trainers`, { trainerId }),
  unlinkTrainer: (batchId, trainerId) =>
    api.delete(`/api/batches/${batchId}/trainers/${trainerId}`),
  getMyStudents: () => api.get('/api/batches/mine/students'),
};

// Enrollments API
export const enrollmentsAPI = {
  getAll: () => api.get('/api/enrollments'),
  getById: (id) => api.get(`/api/enrollments/${id}`),
  getByStudent: (studentId) => api.get(`/api/enrollments/student/${studentId}`),
  getByBatch: (batchId) => api.get(`/api/enrollments/batch/${batchId}`),
  create: (data) => api.post('/api/enrollments', data),
  /** Closes the source enrollment and creates a new one in the target batch (same or different course). */
  changeBatch: (data) => api.post('/api/enrollments/change-batch', data),
  update: (id, data) => api.put(`/api/enrollments/${id}`, data),
  delete: (id) => api.delete(`/api/enrollments/${id}`),
};

// Interview Readiness API
export const interviewReadinessAPI = {
  getReady: () => api.get('/api/interview-readiness/ready'),
  getByEnrollment: (enrollmentId) => api.get(`/api/interview-readiness/enrollment/${enrollmentId}`),
  upsert: (enrollmentId, data) => api.post(`/api/interview-readiness/enrollment/${enrollmentId}`, data),
  update: (enrollmentId, data) => api.put(`/api/interview-readiness/enrollment/${enrollmentId}`, data),
};

// Placements API
export const placementsAPI = {
  getAll: () => api.get('/api/placements'),
  getById: (id) => api.get(`/api/placements/${id}`),
  getStatistics: (params) => api.get('/api/placements/statistics', { params }),
  create: (data) => api.post('/api/placements', data),
  update: (id, data) => api.put(`/api/placements/${id}`, data),
  delete: (id) => api.delete(`/api/placements/${id}`),
  getOpportunities: (enrollmentId) => api.get(`/api/placements/enrollment/${enrollmentId}/opportunities`),
  createOpportunity: (enrollmentId, data) => api.post(`/api/placements/enrollment/${enrollmentId}/opportunities`, data),
};

// Training Concerns API
export const trainingConcernsAPI = {
  getAll: (params) => api.get('/api/training-concerns', { params }),
  getById: (id) => api.get(`/api/training-concerns/${id}`),
  getStatistics: (params) => api.get('/api/training-concerns/statistics', { params }),
  create: (data) => api.post('/api/training-concerns', data),
  update: (id, data) => api.put(`/api/training-concerns/${id}`, data),
  delete: (id) => api.delete(`/api/training-concerns/${id}`),
};

// Counselling API
export const counsellingAPI = {
  getAll: (params) => api.get('/api/counselling', { params }),
  getById: (id) => api.get(`/api/counselling/${id}`),
  getFollowUp: () => api.get('/api/counselling/follow-up'),
  create: (data) => api.post('/api/counselling', data),
  update: (id, data) => api.put(`/api/counselling/${id}`, data),
  delete: (id) => api.delete(`/api/counselling/${id}`),
};

// Reporting API
export const reportingAPI = {
  getBatchStatistics: (batchId) => api.get(`/api/reporting/batch/${batchId}`),
  getPlacementStatistics: (params) => api.get('/api/reporting/placement', { params }),
  getModuleCompletion: (batchId) => api.get(`/api/reporting/batch/${batchId}/modules`),
  getInterviewReadiness: (batchId) => api.get(`/api/reporting/batch/${batchId}/interview-readiness`),
  getTrainingConcerns: (params) => api.get('/api/reporting/training-concerns', { params }),
  getStudentReport: (studentId) => api.get(`/api/reporting/student/${studentId}`),
  getStudentAnalytics: (params) => api.get('/api/reporting/analytics', { params }),
};

export const dataStagingAPI = {
  ingest: (body) => api.post('/api/data-staging/ingest', body),
  ingestUpload: (formData) => api.post('/api/data-staging/ingest-upload', formData),
  listBatches: (params) => api.get('/api/data-staging/batches', { params }),
  getBatch: (id) => api.get(`/api/data-staging/batches/${id}`),
  listDocuments: (params) => api.get('/api/data-staging/documents', { params }),
  getDocument: (id) => api.get(`/api/data-staging/documents/${id}`),
  reviewQueue: (params) => api.get('/api/data-staging/review-queue', { params }),
  evaluate: (id) => api.post(`/api/data-staging/documents/${id}/evaluate`),
  apply: (id, body) => api.post(`/api/data-staging/documents/${id}/apply`, body || {}),
  decide: (id, body) => api.post(`/api/data-staging/documents/${id}/decide`, body || {}),
  decideCluster: (id, body) =>
    api.post(`/api/data-staging/documents/${id}/decide-cluster`, body || {}),
  duplicateCluster: (id) => api.get(`/api/data-staging/documents/${id}/duplicate-cluster`),
};

// Module progress (per-enrollment Completed / Ongoing)
export const moduleProgressAPI = {
  getBatchMatrix: (batchId) => api.get(`/api/module-progress/batch/${batchId}`),
  upsertEnrollmentProgress: (enrollmentId, data) =>
    api.put(`/api/module-progress/enrollment/${enrollmentId}`, data),
};

// Reference Data API
export const referenceAPI = {
  getCourseCategories: () => api.get('/api/course-categories'),
  getDomains: () => api.get('/api/domains'),
  getCourses: () => api.get('/api/courses'),
  getCompanies: () => api.get('/api/companies'),
  getCounselors: () => api.get('/api/counselors'),
};

// Course Categories API
export const courseCategoriesAPI = {
  getAll: () => api.get('/api/course-categories'),
  getById: (id) => api.get(`/api/course-categories/${id}`),
  getByCode: (code) => api.get(`/api/course-categories/code/${code}`),
  getSections: (id) => api.get(`/api/course-categories/${id}/sections`),
  create: (data) => api.post('/api/course-categories', data),
  update: (id, data) => api.put(`/api/course-categories/${id}`, data),
  updateSections: (id, data) => api.put(`/api/course-categories/${id}/sections`, data),
};

// Courses API
export const coursesAPI = {
  getAll: (params) => api.get('/api/courses', { params }),
  getById: (id) => api.get(`/api/courses/${id}`),
  getByCategory: (categoryId) => api.get(`/api/courses?categoryId=${categoryId}`),
  getByDeliveryMode: (mode) => api.get(`/api/courses?deliveryMode=${mode}`),
  create: (data) => api.post('/api/courses', data),
  update: (id, data) => api.put(`/api/courses/${id}`, data),
  delete: (id) => api.delete(`/api/courses/${id}`),
  addModule: (id, moduleId, order) => api.post(`/api/courses/${id}/modules`, { moduleId, order }),
  removeModule: (id, moduleId) => api.delete(`/api/courses/${id}/modules/${moduleId}`),
  reorderModules: (id, moduleOrders) => api.put(`/api/courses/${id}/modules/reorder`, { moduleOrders }),
};

// Modules API
export const modulesAPI = {
  getAll: (params) => api.get('/api/modules', { params }),
  getActive: (params) => api.get('/api/modules', { params: { ...params, isActive: true } }),
  getById: (id) => api.get(`/api/modules/${id}`),
  getWithCourses: (id) => api.get(`/api/modules/${id}/courses`),
  create: (data) => api.post('/api/modules', data),
  update: (id, data) => api.put(`/api/modules/${id}`, data),
  delete: (id) => api.delete(`/api/modules/${id}`),
  search: (query) => api.get('/api/modules', { params: { search: query } }),
};


// Mock Interviews API
export const mockInterviewsAPI = {
  getByEnrollment: (enrollmentId) => api.get(`/api/mock-interviews/enrollment/${enrollmentId}`),
  getById: (id) => api.get(`/api/mock-interviews/${id}`),
  getByCategory: (category) => api.get(`/api/mock-interviews/category/${category}`),
  getByModule: (moduleName) => api.get(`/api/mock-interviews/module/${moduleName}`),
  upsert: (data) => api.post('/api/mock-interviews', data),
  delete: (id) => api.delete(`/api/mock-interviews/${id}`),
};

// Testimonies API
export const testimoniesAPI = {
  getByEnrollment: (enrollmentId) => api.get(`/api/testimonies/enrollment/${enrollmentId}`),
  getById: (id) => api.get(`/api/testimonies/${id}`),
  getWithVideo: (received = true) => api.get(`/api/testimonies/video/${received}`),
  getWithText: (received = true) => api.get(`/api/testimonies/text/${received}`),
  upsert: (data) => api.post('/api/testimonies', data),
};

// Auth (login / register) — use shared api client for interceptors
export const authAPI = {
  login: (email, password, organizationId) =>
    api.post('/api/auth/login', {
      email,
      password,
      ...(organizationId ? { organizationId } : {}),
    }),
  register: (data) => api.post('/api/auth/register', data),
  previewStudentId: (params) => api.get('/api/auth/student-id-preview', { params }),
  session: () => api.get('/api/auth/session'),
};

// App users (Mongo-style user records for admin edit flows)
export const usersAPI = {
  getByEmail: (email) => api.get(`/api/users/email/${encodeURIComponent(email)}`),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  changePassword: (id, data) => api.put(`/api/users/${id}/password`, data),
  filterStudents: (config) => api.get('/api/users/students/filter', config),
};

// Masters dropdowns (legacy /masters routes)
export const mastersAPI = {
  list: (type) => api.get(`/api/masters/${encodeURIComponent(type)}`),
  createCompany: (data) => api.post('/api/masters/company', data),
};

export const trainingCentersAPI = {
  list: (params) => api.get('/api/training-centers', { params }),
  create: (data) => api.post('/api/training-centers', data),
  update: (id, data) => api.put(`/api/training-centers/${id}`, data),
  remove: (id) => api.delete(`/api/training-centers/${id}`),
};

export const heardFromAPI = {
  getAll: () => api.get('/api/heardfrom'),
};

// Student deletion (admin)
export const studentDeletionAPI = {
  bulkDelete: (payload) => api.post('/api/students/delete/bulk-delete', payload, { timeout: 120000 }),
  bulkSoftDelete: (payload) => api.post('/api/students/delete/bulk-soft-delete', payload, { timeout: 120000 }),
  softDelete: (id, payload) => api.patch(`/api/students/delete/${id}/soft-delete`, payload, { timeout: 30000 }),
  hardDelete: (id, payload) => api.delete(`/api/students/delete/${id}`, { data: payload, timeout: 30000 }),
};

// Dashboard summary (numeric stats — not the welcome payload at GET /api/dashboard)
export const dashboardAPI = {
  getStats: () =>
    api.get('/api/dashboard/stats', {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    }),
};

// Dynamic filter options (student filter UI)
export const filterOptionsAPI = {
  getFields: () => api.get('/api/filter-options/fields'),
  getOptionsForField: (fieldName) => api.get(`/api/filter-options/${encodeURIComponent(fieldName)}`),
};

export const csvImportAPI = {
  getTemplate: () => api.get('/api/csv-import/template'),
  upload: (formData) =>
    api.post('/api/csv-import/upload', formData, { timeout: 600000 }),
};

export const pmaImportAPI = {
  getTemplateMeta: () => api.get('/api/pma-import/template'),
  getLabCapacity: () => api.get('/api/pma-import/lab-capacity'),
  ingestLabDiscovery: (formData) =>
    api.post('/api/pma-import/lab-discovery', formData, { timeout: 60000 }),
  runLabDiscovery: () => api.post('/api/pma-import/lab-discovery/run', {}, { timeout: 20000 }),
  getLabDiscoveryProgress: () => api.get('/api/pma-import/lab-discovery/progress', { timeout: 15000 }),
  getLabUserInventory: () => api.get('/api/pma-import/lab-user-inventory'),
  runLabUserInventory: () => api.post('/api/pma-import/lab-user-inventory/run', {}, { timeout: 20000 }),
  getLabUserInventoryProgress: () => api.get('/api/pma-import/lab-user-inventory/progress', { timeout: 15000 }),
  listStudents: (params) => api.get('/api/pma-import/students', { params }),
  previewTemplate: (studentIds = [], policy = {}) =>
    api.get('/api/pma-import/template/preview', {
      params: {
        ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
        ...policy,
      },
      timeout: 60000,
    }),
  downloadTemplate: (studentIds = [], policy = {}) =>
    api.get('/api/pma-import/template.xlsx', {
      params: {
        ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
        ...policy,
      },
      responseType: 'blob',
      timeout: 60000,
    }),
  generateLabUsers: (studentIds = [], policy = {}) =>
    api.post(
      '/api/pma-import/generate',
      {},
      {
        params: {
          ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
          ...policy,
        },
        timeout: 120000,
      }
    ),
  listGenerated: () => api.get('/api/pma-import/generated'),
  previewGenerate: (studentIds = [], policy = {}) =>
    api.get('/api/pma-import/generate/preview', {
      params: {
        ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
        ...policy,
      },
      timeout: 60000,
    }),
  downloadUserGen: (studentIds = [], policy = {}) =>
    api.get('/api/pma-import/UserGen.xlsx', {
      params: {
        ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
        ...policy,
      },
      responseType: 'blob',
      timeout: 60000,
    }),
  sendToPma: (studentIds = [], policy = {}, requestConfig = {}) =>
    api.post(
      '/api/pma-import/infrauser/generatestd',
      {},
      {
        params: {
          ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
          ...policy,
        },
        timeout: requestConfig.timeout ?? 180000,
      }
    ),
  deleteFromLab: (studentIds = [], policy = {}, requestConfig = {}) =>
    api.post(
      '/api/pma-import/infrauser/deletestd',
      {},
      {
        params: {
          ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
          ...policy,
        },
        timeout: requestConfig.timeout ?? 180000,
      }
    ),
  setLabAccess: (action, studentIds = [], policy = {}, requestConfig = {}) =>
    api.post(
      action === 'enable' ? '/api/pma-import/infrauser/enablestd' : '/api/pma-import/infrauser/disablestd',
      {},
      {
        params: {
          ...(studentIds.length ? { studentIds: studentIds.join(',') } : {}),
          ...policy,
        },
        timeout: requestConfig.timeout ?? 180000,
      }
    ),
  upload: (formData, policy = {}) =>
    api.post('/api/pma-import/upload', formData, {
      params: policy,
      timeout: 600000,
    }),
  listReservedUnixPresets: () => api.get('/api/pma-import/reserved-unix/presets'),
  previewReservedUnix: (plan) =>
    api.post('/api/pma-import/reserved-unix/preview', plan, { timeout: 60000 }),
  importReservedUnix: (plan) =>
    api.post('/api/pma-import/reserved-unix', plan, { timeout: 120000 }),
};

export const veBulkImportAPI = {
  getPreview: () => api.get('/api/ve-import/preview'),
  run: (formData) =>
    api.post('/api/ve-import/run', formData, { timeout: 120000 }),
  runFromExcel: (formData) =>
    api.post('/api/ve-import/from-excel', formData, { timeout: 600000 }),
  exportExcel: () =>
    api.get('/api/ve-import/export-excel', {
      responseType: 'blob',
      timeout: 600000,
    }),
  exportExcelComplete: () =>
    api.get('/api/ve-import/export-excel-complete', {
      responseType: 'blob',
      timeout: 600000,
    }),
  getJob: (jobId) => api.get(`/api/ve-import/jobs/${jobId}`),
  getActiveJob: () => api.get('/api/ve-import/jobs/active'),
};

export const labCycleAPI = {
  getWatch: () => api.get('/api/lab-cycle', { timeout: 60000 }),
  act: (unixUsername, action, { keepBackup } = {}) =>
    api.post(
      '/api/lab-cycle/action',
      {
        unixUsername,
        action,
        ...(action === 'delete' ? { keepBackup: keepBackup === true } : {}),
      },
      { timeout: 30000 }
    ),
  actionProgress: (jobId) =>
    api.get('/api/lab-cycle/action-progress', { params: { jobId }, timeout: 15000 }),
};

export const labFleetAPI = {
  get: () => api.get('/api/lab-fleet', { timeout: 60000 }),
  progress: () => api.get('/api/lab-fleet/progress'),
  scan: () => api.post('/api/lab-fleet/scan', {}, { timeout: 30000 }),
  enable: ({ unixUsername, alias, home }) =>
    api.post('/api/lab-fleet/enable', { unixUsername, alias, home }, { timeout: 180000 }),
  similar: ({ unixUsername, alias, home, q }) =>
    api.get('/api/lab-fleet/similar', { params: { unixUsername, alias, home, q: q || undefined }, timeout: 60000 }),
  merge: ({ unixUsername, alias, home, studentId }) =>
    api.post('/api/lab-fleet/merge', { unixUsername, alias, home, studentId }, { timeout: 60000 }),
  exportExcel: (params) =>
    api.get('/api/lab-fleet/export', { params, responseType: 'blob', timeout: 120000 }),
  homeBackups: () => api.get('/api/lab-fleet/home-backups', { timeout: 120000 }),
  removeHomeBackup: ({ alias, path }) =>
    api.post('/api/lab-fleet/home-backups/remove', { alias, path, confirm: true }, { timeout: 60000 }),
};

export const serverCapacityAPI = {
  overview: (params) => api.get('/api/server-capacity', { params, timeout: 60000 }),
  settings: () => api.get('/api/server-capacity/settings'),
  saveSettings: (assumptions) => api.put('/api/server-capacity/settings', { assumptions }, { timeout: 60000 }),
  preview: (body) => api.post('/api/server-capacity/preview', body, { timeout: 60000 }),
  plans: () => api.get('/api/server-capacity/plans'),
  save: (body) => api.post('/api/server-capacity/plans', body, { timeout: 60000 }),
  update: (id, body) => api.patch(`/api/server-capacity/plans/${id}`, body, { timeout: 60000 }),
  approve: (id, note) => api.post(`/api/server-capacity/plans/${id}/approve`, { note }, { timeout: 60000 }),
  exportCsv: (id) => api.get(`/api/server-capacity/plans/${id}/export`, { responseType: 'blob', timeout: 60000 }),
};

export const labHostsAPI = {
  list: () => api.get('/api/lab-hosts'),
  listAll: () => api.get('/api/lab-hosts', { params: { all: '1' } }),
  create: (data) => api.post('/api/lab-hosts', data),
  update: (id, data) => api.patch(`/api/lab-hosts/${encodeURIComponent(id)}`, data),
};

export const accessAPI = {
  getMyAccess: () => api.get('/api/access/me'),
  getPermissions: () => api.get('/api/access/permissions'),
  getLevels: () => api.get('/api/access/levels'),
  createLevel: (data) => api.post('/api/access/levels', data),
  updateLevel: (id, data) => api.put(`/api/access/levels/${id}`, data),
  deleteLevel: (id) => api.delete(`/api/access/levels/${id}`),
  getUsers: () => api.get('/api/access/users'),
  updateUser: (id, data) => api.patch(`/api/access/users/${id}`, data),
};

export const creationRequestsAPI = {
  catalog: (config) => api.get('/api/creation-requests/catalog', config),
  objectiveSuggestions: (config) => api.get('/api/creation-requests/objective-suggestions', config),
  submit: (data, config) => api.post('/api/creation-requests', data, config),
  list: (params) => api.get('/api/creation-requests', { params }),
  get: (id) => api.get(`/api/creation-requests/${id}`),
  approve: (id, data) => api.post(`/api/creation-requests/${id}/approve`, data),
  reject: (id, data) => api.post(`/api/creation-requests/${id}/reject`, data),
  getSettings: () => api.get('/api/creation-requests/settings'),
  updateSettings: (data) => api.patch('/api/creation-requests/settings', data),
};

export const notificationsAPI = {
  getSettings: () => api.get('/api/notifications/settings'),
  updateSettings: (data) => api.put('/api/notifications/settings', data),
  preview: (data) => api.post('/api/notifications/preview', data),
  send: (data) => api.post('/api/notifications/send', data),
  listCampaigns: (params) => api.get('/api/notifications/campaigns', { params }),
  getCampaign: (id) => api.get(`/api/notifications/campaigns/${id}`),
  studentHistory: (studentId, params) =>
    api.get(`/api/notifications/students/${encodeURIComponent(studentId)}/history`, { params }),
  getInbox: (params) => api.get('/api/notifications/inbox', { params }),
  markInboxRead: (data) => api.post('/api/notifications/inbox/read', data),
  getMyHistory: (params) => api.get('/api/notifications/me/history', { params }),
};

export const featureFlagsAPI = {
  list: () => api.get('/api/feature-flags'),
  update: (key, data) => api.patch(`/api/feature-flags/${encodeURIComponent(key)}`, data),
  status: (keys) =>
    api.get('/api/feature-flags/status', {
      params: keys?.length ? { keys: keys.join(',') } : undefined,
    }),
};

export const databaseBackupsAPI = {
  list: () => api.get('/api/database-backups'),
  snapshot: (data) => api.post('/api/database-backups/snapshot', data || {}, { timeout: 600000 }),
  upload: (formData) =>
    api.post('/api/database-backups/upload', formData, { timeout: 600000 }),
  restore: (id, data) =>
    api.post(`/api/database-backups/${encodeURIComponent(id)}/restore`, data, { timeout: 600000 }),
  remove: (id) => api.delete(`/api/database-backups/${encodeURIComponent(id)}`),
  download: (id) =>
    api.get(`/api/database-backups/${encodeURIComponent(id)}/download`, {
      responseType: 'blob',
      timeout: 600000,
    }),
};

// Location helpers
export const locationsAPI = {
  get: (pathSuffix = '') => api.get(`/api/locations${pathSuffix}`),
};

export const locationDetectionAPI = {
  detect: (body, requestConfig = { timeout: 10000 }) =>
    api.post('/api/location-detection/detect', body, requestConfig),
  getSuggestions: (config) => api.get('/api/location-detection/suggestions', config),
  getHierarchy: (config) => api.get('/api/location-detection/hierarchy', config),
};

export default api;

